import React, { useMemo, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FaUsers, FaCheckCircle, FaThumbsDown } from 'react-icons/fa';
import { getCadenceOption, getCadenceColor } from '../constants';
import { Container } from './styles';
import SummaryCard from '~/_shared/components/SummaryCard';
import SummarySkeleton from './SummarySkeleton';
import { loadCadenceDetailedSummary } from '../actions';

// Helper function to create summary object
var createSummary = function(title, icon, done, color, tooltip, columnId, additionalProps) {
  var result = {
    title: title,
    icon: icon,
    done: done,
    undone: 0,
    percentage: "100%",
    color: color,
    tooltip: tooltip,
    columnId: columnId
  };
  if (additionalProps) {
    Object.assign(result, additionalProps);
  }
  return result;
};

var Summary = function(props) {
  var selectedCadence = props.selectedCadence;
  var propSelectedCadenceData = props.selectedCadenceData;
  var onSummaryCardClick = props.onSummaryCardClick;

  var dispatch = useDispatch();
  var columns = useSelector(function(state) { return state.socialSelling.columns; });
  var isLoading = useSelector(function(state) { return state.socialSelling.isLoading; });
  var allCadences = useSelector(function(state) { return state.settings['socialSelling.cadence']; });
  var selectedMonth = useSelector(function(state) { return state.socialSelling.selectedMonth; });
  var cadenceDetailedSummary = useSelector(function(state) { return state.socialSelling.cadenceDetailedSummary; });

  // Get selected cadence data
  var selectedCadenceData = useMemo(function() {
    if (propSelectedCadenceData) return propSelectedCadenceData;

    // Fallback to calculating it here (for backward compatibility)
    if (!allCadences) return null;

    var cadenceId = selectedCadence !== null ? selectedCadence : (allCadences[0] && allCadences[0].id || null);
    if (!cadenceId) return null;

    var cadence = allCadences.find(function(c) { return c.id === cadenceId; });
    if (!cadence) return null;

    try {
      var parsedValue = JSON.parse(cadence.value);
      return {
        name: parsedValue.name,
        data: parsedValue.data
      };
    } catch (e) {
      return null;
    }
  }, [propSelectedCadenceData, allCadences, selectedCadence]);

  // Check if summary needs refresh
  var shouldRefreshSummary = cadenceDetailedSummary && cadenceDetailedSummary.shouldRefresh;

  // Extract primitive values from selectedCadenceData to avoid object reference issues
  var cadenceName = selectedCadenceData && selectedCadenceData.name;

  // Use ref to track last fetched params without causing re-renders
  var lastFetchedRef = useRef({ cadenceName: null, date: null });

  // Load detailed summary from API when cadence or date changes
  useEffect(function() {
    if (!cadenceName) {
      return;
    }

    // Check if params actually changed
    var paramsChanged = lastFetchedRef.current.cadenceName !== cadenceName ||
      lastFetchedRef.current.date !== selectedMonth;

    if (paramsChanged) {
      lastFetchedRef.current = { cadenceName: cadenceName, date: selectedMonth };
      dispatch(loadCadenceDetailedSummary(cadenceName, selectedMonth));
    }
  }, [dispatch, cadenceName, selectedMonth]);

  // Separate effect for refresh flag - only triggers when shouldRefreshSummary becomes true
  useEffect(function() {
    if (!shouldRefreshSummary || !cadenceName) {
      return;
    }

    dispatch(loadCadenceDetailedSummary(cadenceName, selectedMonth));
  }, [dispatch, shouldRefreshSummary, cadenceName, selectedMonth]);

  // Generate summary data from API response
  var summaryDataCalculated = useMemo(function() {
    if (!columns || columns.length === 0 || !selectedCadenceData) {
      return [];
    }

    var apiData = cadenceDetailedSummary && cadenceDetailedSummary.data;

    // Build summary cards - we'll filter out column 2 since we merge it with column 1
    var result = [];
    var hasAddedLeadsCard = false;

    columns.forEach(function(column) {
      // Skip column 2 - we merge it with column 1 into a single "Leads" card
      if (column.id === 2) {
        return;
      }

      // Leads card - leads created on selected date (inicio + leads columns)
      if (column.id === 1) {
        if (!hasAddedLeadsCard) {
          var totalLeads = apiData ? (apiData.inicio + apiData.leads) : 0;
          result.push(createSummary(
            "Leads",
            <FaUsers />,
            totalLeads,
            "#1890ff",
            'Leads criados na data selecionada com a cadência "' + selectedCadenceData.name + '"',
            column.id
          ));
          hasAddedLeadsCard = true;
        }
        return;
      }

      // Positivo column - from API with date filter applied
      if (column.id === 5) {
        var positiveLeads = apiData ? apiData.positivo : 0;
        result.push(createSummary(
          "Positivo",
          <FaCheckCircle />,
          positiveLeads,
          "#52c41a",
          'Total de leads na coluna Positivo com a cadência "' + selectedCadenceData.name + '" (filtrado por data)',
          column.id
        ));
        return;
      }

      // Negativo column - from API with date filter applied
      if (column.id === 6) {
        var negativeLeads = apiData ? apiData.negativo : 0;
        result.push(createSummary(
          "Negativo",
          <FaThumbsDown />,
          negativeLeads,
          "#f5222d",
          'Total de leads na coluna Negativo com a cadência "' + selectedCadenceData.name + '" (filtrado por data)',
          column.id
        ));
        return;
      }

      // Step column - count from API steps data
      if (column.type === 'step' && column.stepData) {
        var stepUid = column.stepData.uid;
        var stepCount = 0;

        if (apiData && apiData.steps) {
          var foundStep = apiData.steps.find(function(s) { return s.uid === stepUid; });
          if (foundStep) {
            stepCount = foundStep.count;
          }
        }

        // Handle step name generation
        var stepName;
        if (typeof column.stepData.type === 'string' && isNaN(parseInt(column.stepData.type))) {
          stepName = column.stepData.type;
        } else {
          var stepType = parseInt(column.stepData.type);
          stepName = getCadenceOption(stepType) || column.stepData.type;
        }

        result.push(createSummary(
          stepName,
          null,
          stepCount,
          getCadenceColor(column.stepData.type) || "#007bff",
          'Total de leads que completaram a etapa ' + stepName + ' (dia ' + column.stepData.day + ')',
          column.id,
          {
            day: column.stepData.day,
            script: column.stepData.script
          }
        ));
        return;
      }

      // Fallback for any other columns
      var columnLeads = column.data ? column.data.length : 0;
      result.push(createSummary(
        column.name || "Coluna",
        null,
        columnLeads,
        "#666666",
        'Total de leads em ' + column.name,
        column.id
      ));
    });

    return result;
  }, [columns, selectedCadenceData, cadenceDetailedSummary]);

  // Show loading if either leads or summary is loading
  var summaryIsLoading = cadenceDetailedSummary && cadenceDetailedSummary.isLoading;
  if (isLoading || summaryIsLoading) {
    return <SummarySkeleton />;
  }

  if (!selectedCadenceData) {
    return null;
  }

  return (
    <Container>
      {summaryDataCalculated.map(function(item, index) {
        return (
          <SummaryCard
            key={item.columnId + '-' + index}
            item={item}
            onClick={onSummaryCardClick}
          />
        );
      })}
    </Container>
  );
};

export default React.memo(Summary);
